import {
  Badge,
  Button,
  Col,
  Descriptions,
  DescriptionsProps,
  Form,
  Input,
  List,
  Result,
  Row,
  Select,
  Skeleton,
  Space,
  Typography,
} from 'antd';
import Page from 'components/page';
import useGetAllGroups from 'hooks/data/useGetAllGroups';
import useGetUser from 'hooks/data/useGetUser';
import useUpdateUser from 'hooks/update/useUpdateUser';
import useToastContext from 'hooks/useToastContext';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { camelCaseObjectToSnakeCase } from 'utils/camelCaseToSnakeCase';
import { getFormatedDateWithHours } from 'utils/date';
import { HttpStatusCode } from '../../constants';

type EditUserFormInputs = {
  userName: string;
  email: string;
  firstName: string;
  lastName: string;
  groups: string[];
};

const UserPage = () => {
  const { userId, issuer } = useParams<{ userId: string; issuer: string }>();
  const navigate = useNavigate();
  const toast = useToastContext();
  const { isLoadingUser, updateUser } = useUpdateUser();

  const [editMode, setEditMode] = useState<boolean>(false);
  const [form] = Form.useForm();

  const { user, isLoading, isError } = useGetUser(userId);
  const { groups } = useGetAllGroups(0, 100);

  const userItems: DescriptionsProps['items'] = [
    {
      key: '1',
      label: 'ID',
      children: user?.id,
    },
    {
      key: '2',
      label: 'Nombre de usuario',
      children: !editMode ? (
        user?.user_name
      ) : (
        <Form.Item<EditUserFormInputs>
          name="userName"
          initialValue={user?.user_name}
          rules={[{ required: true, message: 'Por favor ingresar un nombre de usuario.' }]}
        >
          <Input />
        </Form.Item>
      ),
    },
    {
      key: '3',
      label: 'Nombre',
      children: !editMode ? (
        user?.first_name
      ) : (
        <Form.Item<EditUserFormInputs>
          name="firstName"
          initialValue={user?.first_name}
          rules={[{ required: true, message: 'Por favor ingresar un nombre.' }]}
        >
          <Input />
        </Form.Item>
      ),
    },
    {
      key: '4',
      label: 'Apellido',
      children: !editMode ? (
        user?.last_name
      ) : (
        <Form.Item<EditUserFormInputs>
          name="lastName"
          initialValue={user?.last_name}
          rules={[{ required: true, message: 'Por favor ingresar un appelido.' }]}
        >
          <Input />
        </Form.Item>
      ),
    },
    {
      key: '5',
      label: 'Email',
      children: !editMode ? (
        user?.email
      ) : (
        <Form.Item<EditUserFormInputs>
          name="email"
          initialValue={user?.email}
          rules={[
            { required: true, message: 'Por favor ingresar un email.' },
            { type: 'email', message: 'Por favor ingresar un email valido.' },
          ]}
        >
          <Input />
        </Form.Item>
      ),
    },
    {
      key: '6',
      label: 'Fecha de Creacion',
      children: getFormatedDateWithHours(user?.created_timestamp),
    },
    {
      key: '7',
      label: 'Estado',
      children: (
        <Badge status={user?.enabled ? 'success' : 'error'} text={user?.enabled ? 'Activado' : 'Desactivado'} />
      ),
    },
    {
      key: '8',
      label: 'Grupos',
      children: !editMode ? (
        <List
          itemLayout="horizontal"
          dataSource={user?.groups}
          renderItem={(item) => (
            <List.Item
              actions={[
                <Button
                  key="btn-see-more"
                  type="link"
                  onClick={() => navigate(`/${issuer}/settings/groups/${item?.id}`)}
                >
                  Ver
                </Button>,
              ]}
            >
              <List.Item.Meta title={item?.name} />
            </List.Item>
          )}
        />
      ) : (
        <Form.Item<EditUserFormInputs>
          name="groups"
          rules={[{ required: true, message: 'Por favor seleccionar uno.' }]}
          initialValue={user?.groups?.map((group) => group?.id)}
        >
          <Select mode="multiple" options={groups?.map((group) => ({ label: group?.name, value: group?.id }))} />
        </Form.Item>
      ),
    },
  ];

  return (
    <Page title={`Usuario ${user?.user_name ?? ''}`}>
      <Skeleton loading={isLoading}>
        <Row>
          <Col span={24}>
            {isError && (
              <Result
                status="warning"
                title="Hubo un problema al cargar la pagina."
                extra={
                  <Button type="primary" key="console" onClick={() => navigate(0)}>
                    Reintentar
                  </Button>
                }
              />
            )}
            {!isError && (
              <>
                <Form
                  form={form}
                  onFinish={async (data) => {
                    try {
                      const res = await updateUser(user?.id, camelCaseObjectToSnakeCase(data));
                      if ([HttpStatusCode.NoContent, HttpStatusCode.Ok].includes(res.status)) {
                        toast.success('Operacion exitosa!', 'El usuario se ha actualizado con exito.');
                        form.resetFields();
                        setEditMode(false);
                      } else {
                        throw Error('Status code different 204');
                      }
                    } catch (e) {
                      toast.error('Error!', 'El usuario no se ha actualizado, por favor reintentar nuevamente.');
                      console.error(e);
                    }
                  }}
                >
                  <Descriptions
                    title={<Typography.Title level={2}>Detalles</Typography.Title>}
                    layout="horizontal"
                      extra={
                        editMode ? (
                          <Space>
                            <Button
                              type="text"
                              danger
                              onClick={() => {
                                form.resetFields();
                                setEditMode(false);
                              }}
                            >
                              Cancelar
                            </Button>
                            <Button type="primary" loading={isLoadingUser} onClick={() => form.submit()}>
                              Guardar
                            </Button>
                          </Space>
                        ) : (
                          <Button type="primary" onClick={() => setEditMode(true)}>
                            Editar
                          </Button>
                        )
                      }
                    bordered
                    items={userItems}
                  />
                </Form>
              </>
            )}
          </Col>
        </Row>
      </Skeleton>
    </Page>
  );
};
export default UserPage;
